import React, { useEffect } from 'react';
import { Box, Grid, makeStyles, Typography } from 'housecall-ui';

import { useBookingConfirmPaymentData } from '../../hooks/useBookingConfirmationMutation';
import { useOrgData } from '../../hooks/useOrgData';
import { useRedirectAfterConfirmation } from '../../hooks/useRedirectAfterConfirmation';
import { useOnlineBookingReducer } from '../../state/useOnlineBookingReducer';
import { hasPaymentStep } from '../../state/utils';
import { CompanyLogo } from '../CompanyLogo';
import { CountDown } from '../CountDown';
import { Layout } from '../Layout';

const REDIRECT_TIMEOUT = 4000;

const useStyles = makeStyles(() => ({
  content: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    textAlign: 'center',
  },
}));

const SuccessViewDefault = () => (
  <Grid item xs={12} sm={8}>
    <Typography gutterBottom variant="h5" align="center">
      Thank you
    </Typography>

    <Typography gutterBottom variant="body1" align="center">
      Your booking was successful. We&apos;ll send you an email confirmation.
    </Typography>
  </Grid>
);

type Props = {
  isPreview?: boolean;
};

const SuccessViewPayment = ({ isPreview = false }: Props) => {
  const { redirect, hasRedirectUrl } = useRedirectAfterConfirmation();
  const canRedirect = hasRedirectUrl && !isPreview;

  useEffect(() => {
    if (!canRedirect) {
      return;
    }
    setTimeout(redirect, REDIRECT_TIMEOUT);
  }, [canRedirect, redirect]);

  return (
    <Layout
      ContentComponent={
        <Box display="flex" justifyContent="center" alignItems="center">
          <Grid item xs={12} sm={10}>
            <CompanyLogo top={false} />
            <Typography gutterBottom variant="h5" align="center">
              Payment accepted!
            </Typography>

            <Typography gutterBottom variant="body1" align="center">
              Thank you for booking with us! Your payment has been successfully processed, and an email confirmation is
              on its way to your inbox.
            </Typography>
          </Grid>
        </Box>
      }
      Buttons={
        canRedirect ? (
          <Box margin="0 auto">
            <Typography variant="subtitle2" align="center" data-testid="ob-countdown">
              Redirecting in <CountDown />
              s...
            </Typography>
          </Box>
        ) : undefined
      }
    />
  );
};

export const SuccessView = () => {
  const {
    state: { initParams, token, cart, tripCharge },
  } = useOnlineBookingReducer();
  const { data: orgData } = useOrgData({ token });
  const isPreview = initParams?.source === 'preview';
  const requiresPayment = Boolean(orgData && hasPaymentStep(cart, orgData, tripCharge));
  const previewWithPayment = isPreview && requiresPayment;

  const { content } = useStyles();
  const paymentData = useBookingConfirmPaymentData();
  const afterPaymentFlow = Boolean(paymentData?.paymentRequired) || previewWithPayment;

  return (
    <Grid container className={content}>
      {afterPaymentFlow ? <SuccessViewPayment isPreview={isPreview} /> : <SuccessViewDefault />}
    </Grid>
  );
};
